import { Component, EventEmitter, Output, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Router } from '@angular/router';
import { AccountsPayableService } from 'src/app/services/accounts-payable.service';
import { ApPermissionService } from 'src/app/services/ap-permission.service';
import { AccountsPayableAction } from 'src/app/models/accounts-payable-actions.model';
import { FileModel } from 'src/app/models/file-model';
import { ToasterService } from 'src/app/utils/toaster.service';
import {
  tap,
  catchError,
  of,
  filter,
  map,
  Subscription,
  forkJoin,
  shareReplay,
} from 'rxjs';
import { HttpEventType, HttpResponse } from '@angular/common/http';

interface FileUploadStatus {
  file: File;
  uploadedFile?: FileModel;
  uploading: boolean;
  error: boolean;
  progress: number;
  uploadedBytes: number;
  totalBytes: number;
}

@Component({
  selector: 'app-bulk-ap-requests-modal',
  templateUrl: './bulk-ap-requests-modal.component.html',
  styleUrls: ['./bulk-ap-requests-modal.component.scss'],
})
export class BulkApRequestsModalComponent implements OnDestroy, OnInit {
  @Output() modalClose = new EventEmitter<FileModel[]>();

  /** Optional files passed when opening the modal (e.g. overview drag-and-drop). */
  initialFiles?: File[];

  files: FileUploadStatus[] = [];
  isDragging = false;
  readonly maxFileSizeMB = 50;
  readonly allowedFileType = 'application/pdf';
  private uploadSubscriptions = new Map<string, Subscription>();

  constructor(
    public bsModalRef: BsModalRef,
    private apService: AccountsPayableService,
    private apPermissionSvc: ApPermissionService,
    private toasterService: ToasterService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.initialFiles?.length) {
      this.handleFiles(this.initialFiles);
      this.initialFiles = undefined;
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(Array.from(files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
      input.value = ''; // Reset input
    }
  }

  private handleFiles(files: File[]): void {
    for (const file of files) {
      // Validate file type (PDF only)
      if (file.type !== this.allowedFileType) {
        this.toasterService.showError(
          `File "${file.name}" is not a PDF file. Only PDF files are allowed.`
        );
        continue;
      }

      // Validate file size (max 50MB)
      const maxSizeBytes = this.maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        this.toasterService.showError(
          `File "${file.name}" exceeds the maximum size of ${this.maxFileSizeMB}MB.`
        );
        continue;
      }

      // Check if file already exists
      if (
        this.files.some(
          (f) => f.file.name === file.name && f.file.size === file.size
        )
      ) {
        this.toasterService.showError(`File "${file.name}" is already added.`);
        continue;
      }

      // Add file to list (not uploading yet - will upload on Create Requests button click)
      this.files.push({
        file,
        uploading: false,
        error: false,
        progress: 0,
        uploadedBytes: 0,
        totalBytes: file.size,
      });
    }
  }

  private uploadFiles(): Promise<void> {
    const filesToUpload = this.files.filter(
      (f) => !f.uploading && !f.uploadedFile && !f.error
    );

    if (filesToUpload.length === 0) {
      return Promise.resolve();
    }

    // Create upload observables for all files
    const uploadObservables = filesToUpload.map((fileStatus) => {
      fileStatus.uploading = true;
      fileStatus.error = false;
      fileStatus.progress = 0;
      fileStatus.uploadedBytes = 0;

      // Create a unique key for this file upload
      const fileKey = this.getFileKey(fileStatus.file);

      // Use the service method for ProcessFile endpoint
      const observable = this.apService
        .processFile(fileStatus.file, {
          reportProgress: true,
          observe: 'events',
        }, 'AccountsPayable')
        .pipe(
          shareReplay(1), // Make it multicast so multiple subscriptions share the same execution
          tap((event: any) => {
            if (event.type === HttpEventType.UploadProgress) {
              // Update progress during upload
              const total = event.total || fileStatus.totalBytes;
              const loaded = event.loaded || 0;

              fileStatus.uploadedBytes = loaded;
              fileStatus.progress = Math.round((loaded / total) * 100);
            } else if (event.type === HttpEventType.Response) {
              // Upload complete - ProcessFile endpoint processes the file
              // Create a FileModel object from the response and file info
              const response = event.body;

              // Create a FileModel object matching the FileModel structure
              const fileModel: FileModel = {
                name: response?.name || fileStatus.file.name,
                webUrl: response?.webUrl || response?.url || '',
                stream: response?.stream || '',
                caption: response?.caption || fileStatus.file.name,
                contentType:
                  response?.contentType ||
                  fileStatus.file.type ||
                  'application/pdf',
                size:
                  response?.size || response?.fileSize || fileStatus.file.size,
                // Include any additional properties from the response
                ...(response || {}),
              };

              fileStatus.uploadedFile = fileModel;
              fileStatus.uploading = false;
              fileStatus.progress = 100;
              fileStatus.uploadedBytes = fileStatus.totalBytes;

              // Clean up subscription on completion
              this.uploadSubscriptions.delete(fileKey);
            }
          }),
          filter((event: any) => event.type === HttpEventType.Response),
          map((event: any) => {
            const response = (event as HttpResponse<any>).body;
            // Create FileModel from response matching FileModel structure
            const fileModel: FileModel = {
              name: response?.name || fileStatus.file.name,
              webUrl: response?.webUrl || response?.url || '',
              stream: response?.stream || response?.base64Stream || '',
              caption: response?.caption || fileStatus.file.name,
              contentType:
                response?.contentType ||
                fileStatus.file.type ||
                'application/pdf',
              size:
                response?.size || response?.fileSize || fileStatus.file.size,
              ...(response || {}),
            };
            return fileModel;
          }),
          catchError((error) => {
            fileStatus.uploading = false;
            fileStatus.error = true;
            fileStatus.progress = 0;
            fileStatus.uploadedBytes = 0;

            // Clean up subscription on error
            this.uploadSubscriptions.delete(fileKey);

            this.toasterService.showError(
              `Failed to upload "${fileStatus.file.name}": ${
                error?.error?.message || error?.message || 'Unknown error'
              }`
            );
            return of(null);
          })
        );

      // Store subscription reference for potential cancellation
      // Subscribe early to start the upload and track progress
      const subscription = observable.subscribe();
      this.uploadSubscriptions.set(fileKey, subscription);

      // Return the observable for forkJoin (will share the same execution due to shareReplay)
      return observable;
    });

    // Wait for all uploads to complete
    // Use forkJoin but catch errors for individual uploads
    return new Promise<void>((resolve) => {
      if (uploadObservables.length === 0) {
        resolve();
        return;
      }

      forkJoin(
        uploadObservables.map((obs) =>
          obs.pipe(
            catchError((error) => {
              // Error already handled in the individual pipe
              return of(null);
            })
          )
        )
      ).subscribe({
        next: () => {
          resolve();
        },
        error: () => {
          // All errors are handled individually, so we still resolve
          resolve();
        },
      });
    });
  }

  private getFileKey(file: File): string {
    // Create a unique key based on file name and size
    return `${file.name}_${file.size}_${file.lastModified}`;
  }

  removeFile(index: number): void {
    const fileStatus = this.files[index];

    // If file is currently uploading, cancel the upload
    if (fileStatus.uploading) {
      const fileKey = this.getFileKey(fileStatus.file);
      const subscription = this.uploadSubscriptions.get(fileKey);

      if (subscription) {
        subscription.unsubscribe();
        this.uploadSubscriptions.delete(fileKey);
      }

      // Reset file status
      fileStatus.uploading = false;
      fileStatus.progress = 0;
      fileStatus.uploadedBytes = 0;
    }

    // Remove file from list
    this.files.splice(index, 1);
  }

  get canCreateRequests(): boolean {
    return (
      this.apPermissionSvc.can(AccountsPayableAction.Create) &&
      this.files.length > 0 &&
      !this.files.some((f) => f.uploading)
    );
  }

  get isUploading(): boolean {
    return this.files.some((f) => f.uploading);
  }

  onCreateRequests(): void {
    if (!this.apPermissionSvc.can(AccountsPayableAction.Create)) {
      return;
    }

    if (this.files.length === 0) {
      return;
    }

    // Check if there are any files that haven't been uploaded yet
    const filesToUpload = this.files.filter(
      (f) => !f.uploadedFile && !f.uploading && !f.error
    );

    if (filesToUpload.length === 0) {
      // All files already uploaded, just close with success
      this.toasterService.showSuccess(
        'Files uploaded successfully. Draft requests are being created. Please check the listing.'
      );
      this.modalClose.emit(
        this.files.filter((f) => f.uploadedFile).map((f) => f.uploadedFile!)
      );
      this.bsModalRef.hide();
      this.router.navigate(['/accounts-payable/list']);
      return;
    }

    // Start uploading all files
    this.uploadFiles().then(() => {
      // Check if all files uploaded successfully
      const allUploaded = this.files.every((f) => f.uploadedFile && !f.error);
      const hasErrors = this.files.some((f) => f.error);

      if (allUploaded && !hasErrors) {
        // All files uploaded successfully
        this.toasterService.showSuccess(
          'Account payable draft request created successfully'
        );
        this.modalClose.emit(
          this.files.filter((f) => f.uploadedFile).map((f) => f.uploadedFile!)
        );
        this.bsModalRef.hide();
        this.router.navigate(['/accounts-payable/list']);
      } else if (hasErrors) {
        // Some files failed, don't close modal
        this.toasterService.showError(
          'Some files failed to upload. Please remove failed files and try again.'
        );
      }
    });
  }

  onClose(): void {
    this.bsModalRef.hide();
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return bytes + ' B';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(0) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    }
  }

  ngOnDestroy(): void {
    // Cancel all ongoing uploads when component is destroyed
    this.uploadSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.uploadSubscriptions.clear();
  }
}
